import { FC, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { renderToStaticMarkup } from "react-dom/server";
import Background from "@/icons/background";
import "@/components/styles/login.css";
import { api } from "@/helper/api";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import PasswordInput from "@/components/forms/inputs/passwordInput";
import { useForm } from "react-hook-form";
import CheckboxWithControl from "@/components/forms/inputs/checkboxWithControl";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import styles from "@/components/forms/registerForm.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import GenericControllerWithInput from "@/components/forms/inputsWithController/GenericController";
import DatePickerGeneric from "@/components/dateTime/DatePickerGeneric";
import { MuiTelInput } from "mui-tel-input";
import { FormControlLabel } from "@mui/material";
import classNames from "classnames";
import dayjs from "dayjs";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import { useSecurePasswordGuideLink } from "@/helper/customHooks/useSecurePasswordGuideLink";

interface Props {
  title?: string;
}

const RegisterForm: FC<Props> = () => {
  const securePasswordGuideLink = useSecurePasswordGuideLink();
  const { handleError, errorModal } = useErrorModal(undefined, {
    helpLinksByField: { password: [securePasswordGuideLink] },
  });
  const [registrationSuccessful, setRegistrationSuccessful] =
    useState<boolean>(false);
  const { t } = useTranslation();
  const { userId, requestToken } = useParams() as {
    userId: string;
    requestToken: string;
  };
  const navigate = useNavigate();
  const theme = useTheme();
  const { handleSubmit, control, watch } = useForm({
    mode: "onChange",
    shouldFocusError: false,
    defaultValues: {
      password: "",
      confirmedPassword: "",
      confirmNewsletter: false,
      confirmPrivacyPolicy: false,
      confirmTelemetry: false,
      birth_date: "",
      phone: "",
    },
  });

  const svgString = encodeURIComponent(renderToStaticMarkup(<Background />));
  const passwordRegex = new RegExp(
    /^(?=.*[0-9])(?=.*[!=?\-_+/&$#*.,:;@]).{10,}$/,
  );

  const newPassword = watch("password");
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField({
      fieldOrder: ["password", "confirmedPassword", "confirmPrivacyPolicy"],
    });

  const loginWrapperStyle = {
    background: `url('data:image/svg+xml;utf8, ${svgString}')`,
  };

  const onSubmit = async (data: any) => {
    const result = {
      password: data.password,
      token: requestToken,
      agree_to_data_collection: data.confirmPrivacyPolicy,
      newsletter_accepted: data.confirmNewsletter,
      telemetry_accepted: data.confirmTelemetry,
      ...(data.birth_date !== "" && {
        birth_date: dayjs(data.birth_date).format("DD.MM.YYYY"),
      }),
      ...(data.phone !== "" && { phone: data.phone }),
    };
    const config = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const additionalRouteParts: any = {};
    additionalRouteParts["reset_password"] = "/";

    try {
      await new Promise<any>((resolve, reject) => {
        api.genericApiRequest({
          entity: "user",
          method: "put",
          entityId: userId,
          additionalRouteParts: additionalRouteParts,
          submitData: result,
          config: config,
          successHandler: (res: any) => {
            resolve(res);
            setRegistrationSuccessful(true);
          },
          failHandler: (error: any, status: any) => {
            if (status === 401) {
              handleError(t("registration_expired_link"));
            } else {
              handleError(error);
            }
            reject(error);
          },
        });
      });
    } catch {
      //errorhandling in this case done in failhandler.
    }
  };

  const formWrapperClass = useMediaQuery(theme.breakpoints.up("sm"))
    ? `${styles.registerFormWrapper} ${styles.registerFormWrapperWide}`
    : styles.registerFormWrapper;

  return (
    <>
      <div className={`${styles.loginWrapper}`} style={loginWrapperStyle}>
        <div className={formWrapperClass}>
          {registrationSuccessful ? (
            <section className="successNotification">
              <h2>
                {t("thank_you")}
                <br /> {t("registration_successful")}
              </h2>
              <p>{t("click_here_to_login")}</p>
              <GenericButton
                className={styles.register_navigateButton}
                onClick={() => navigate("/login/")}
              >
                {t("login")}
              </GenericButton>
            </section>
          ) : (
            <form
              onSubmit={handleSubmit(onSubmit, (errors) => {
                focusFirstInvalidField(errors);
              })}
              className="registerForm"
              noValidate
            >
              <h1 className="heading">{t("register")}</h1>
              <PasswordInput
                validationTargetRef={registerValidationTarget("password")}
                label={
                  <div>
                    <span className={styles.displayBlock}>
                      {t("password_registration")}
                    </span>
                    <span>{t("password_requirements")}</span>
                  </div>
                }
                id={t("password_registration")}
                title={t("password_registration")}
                dynamicLabel={false}
                control={control}
                name={"password"}
                required={true}
                requiredErrorMessage={t("field_required")}
                pattern={passwordRegex}
                patternErrorMessage={t("password_requirements")}
                errorFontColor={"#FFF"}
              />

              <PasswordInput
                validationTargetRef={registerValidationTarget(
                  "confirmedPassword",
                )}
                id={t("password_registration_confirm")}
                title={t("password_registration_confirm")}
                label={t("password_registration_confirm")}
                dynamicLabel={false}
                control={control}
                name={"confirmedPassword"}
                required={true}
                requiredErrorMessage={t("field_required")}
                errorFontColor={"#FFF"}
                validationRule={newPassword}
                validationRuleErrorMessage={t("password_dont_match")}
              />

              <section className={styles.optionalInputWrapper}>
                <GenericControllerWithInput
                  control={control}
                  name="birth_date"
                  renderInput={({ onChange, value }) => {
                    return (
                      <div className={styles.birthDayinputWrapper}>
                        <DatePickerGeneric
                          value={value ?? null}
                          onChange={onChange}
                          label={t("birthdate")}
                          labelClassname={styles.dateLabel}
                          inputClassname={classNames(
                            styles.birthDayInput,
                            styles.noOutline,
                            styles.padding,
                          )}
                          textfieldClassname={classNames(
                            styles.textField,
                            styles.notOutline,
                          )}
                          rootClassnames={styles.noOutline}
                        />
                      </div>
                    );
                  }}
                />
                <GenericControllerWithInput
                  control={control}
                  name="phone"
                  renderInput={({ onChange, value, ...rest }) => {
                    return (
                      <div className={styles.telInputWrapper}>
                        <FormControlLabel
                          labelPlacement="top"
                          sx={{
                            margin: "0 ",
                            width: "100%",
                            alignItems: "start",
                            fontFamily: "inherit !important",
                            color: "#fff",
                          }}
                          disableTypography
                          label={t("phone")}
                          control={
                            <MuiTelInput
                              {...rest}
                              placeholder={t("input_your_phone_number")}
                              defaultCountry="DE"
                              langOfCountryName="DE"
                              value={value || ""}
                              className={classNames(
                                styles.telInput,
                                styles.noOutline,
                              )}
                              onChange={onChange}
                              InputProps={{
                                classes: {
                                  input: styles.padding,
                                },
                              }}
                            />
                          }
                        />
                      </div>
                    );
                  }}
                />
              </section>

              <section className="checkboxWrapper">
                <CheckboxWithControl
                  validationTargetRef={registerValidationTarget(
                    "confirmPrivacyPolicy",
                  )}
                  control={control}
                  id={t("confirm_privacy_policy")}
                  required={true}
                  requiredErrorMessage={t("confirm_privacy_policy_warning")}
                  className={styles.checkboxStyle}
                  name={"confirmPrivacyPolicy"}
                  errorFontColor={"#FFF"}
                  label={
                    <>
                      <span className={styles.checkboxWithControl_labelSpan}>
                        <Trans i18nKey="confirm_privacy_policy">
                          confirm_privacy_policy
                          <Link
                            to="/datenschutz"
                            target="_blank"
                            className="privacyPolicyLink"
                          />
                        </Trans>
                      </span>
                    </>
                  }
                />
                <CheckboxWithControl
                  control={control}
                  id={t("confirm_newsletter")}
                  name={"confirmNewsletter"}
                  className={styles.checkboxStyle}
                  label={
                    <>
                      <span className={styles.checkboxWithControl_labelSpan}>
                        <Trans i18nKey="confirm_newsletter" />
                      </span>
                    </>
                  }
                />
                <CheckboxWithControl
                  id={t("confirm_telemetry")}
                  name={"confirmTelemetry"}
                  className={styles.checkboxStyle}
                  control={control}
                  label={
                    <>
                      <span className={styles.checkboxWithControl_labelSpan}>
                        <Trans i18nKey="confirm_telemetry" />
                      </span>
                    </>
                  }
                />
              </section>
              <div className={styles.centerStyle}>
                <GenericButton
                  type="submit"
                  className={styles.register_submitButton}
                >
                  {t("save")}
                </GenericButton>
              </div>
              <div className={styles.contactUsContainer}>
                <span>
                  {t("contact_if_problems_with_registration") + " "}
                  <a
                    className={styles.emailLink}
                    href="mailto:service@kaerhealth.com"
                  >
                    {t("email_service_kaer")}
                  </a>
                </span>
              </div>
            </form>
          )}
        </div>

        {errorModal}
      </div>
    </>
  );
};

export default RegisterForm;
